import { FC, MouseEvent, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IInsight } from './IInsight';
import { InsightsModel } from './InsightsModel';
import { BeeChatViewModel } from '../helper-bee/BeeChatViewModel';
import { NOT_USEFUL_REASONS } from './notUsefulReasons';

const useStyles = createUseStyles({
    foot: {
        marginTop: 12,
        paddingTop: 12,
        borderTop: '1px solid var(--ion-color-step-100)',
    },
    askRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap' as const,
    },
    askLabel: {
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        flex: 1,
        minWidth: 110,
    },
    thumb: {
        fontSize: 16,
        background: 'var(--ion-background-color)',
        border: '1px solid var(--ion-color-step-200)',
        borderRadius: 999,
        width: 40,
        height: 34,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    promptLabel: {
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        marginBottom: 10,
    },
    promptLabelStrong: {
        fontWeight: 600,
    },
    promptRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    reasons: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 7,
    },
    chip: {
        fontSize: 14,
        background: 'var(--ion-background-color)',
        border: '1px solid var(--ion-color-step-200)',
        color: 'var(--ion-color-step-750)',
        borderRadius: 999,
        padding: '7px 13px',
        cursor: 'pointer',
    },
    chatButton: {
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ion-color-primary-contrast)',
        background: 'var(--ion-color-primary)',
        border: 0,
        borderRadius: 999,
        padding: '8px 15px',
        cursor: 'pointer',
    },
    declineButton: {
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        background: 'none',
        border: 0,
        padding: '6px 6px',
        cursor: 'pointer',
    },
    skip: {
        marginTop: 10,
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
});

type FeedbackStage = 'ask' | 'up-chat' | 'down-reason' | 'hidden';

export interface IInsightFeedbackProps {
    insight: IInsight;
    model: InsightsModel;
    // Where this feedback flow is rendered, so 'Insight Feedback' and 'Insight Dismissed' both
    // distinguish which surface the 👍/👎 came from.
    surface: string;
    // Called once a 👎 reason archives the insight. The list drops it from getActive() so the card just
    // leaves the list; the drawer passes its close helper here since the insight is gone from the list.
    onArchived?: () => void;
}

// The shared "Was this helpful?" flow: 👍 offers to chat it through with Bea, 👎 collects a reason and
// archives the insight. Used by both the Insights-page list card and the insight detail drawer so the two
// surfaces stay identical.
export const InsightFeedback: FC<IInsightFeedbackProps> = ({ insight, model, surface, onArchived }) => {
    const classes = useStyles();
    const beeChat = useInstance(BeeChatViewModel);

    const [stage, setStage] = useState<FeedbackStage>('ask');

    const onThumbUp = (event: MouseEvent) => {
        event.stopPropagation();
        model.sendFeedback({ insight, helpful: true, surface });
        setStage('up-chat');
    };

    const onThumbDown = (event: MouseEvent) => {
        event.stopPropagation();
        setStage('down-reason');
    };

    const onChat = (event: MouseEvent) => {
        event.stopPropagation();
        beeChat.open({ trackingFrom: 'Insight', initialMessage: `Tell me more about the pattern you noticed: ${insight.title}` });
        setStage('hidden');
    };

    const onReason = (event: MouseEvent, note?: string) => {
        event.stopPropagation();
        model.sendFeedback({ insight, helpful: false, surface, note });
        // dismiss removes it from getActive(); the list card leaves the list, the drawer closes.
        model.dismiss(insight, surface);
        if (onArchived) {
            onArchived();
        }
    };

    if (stage === 'hidden') {
        return null;
    }

    if (stage === 'up-chat') {
        return (
            <div className={classes.foot}>
                <div className={classes.promptLabel}>Glad it helped. Want to talk it through with Bea?</div>
                <div className={classes.promptRow}>
                    <button className={classes.chatButton} onClick={onChat} data-id="insight-chat">Chat about this</button>
                    <span className={classes.declineButton} onClick={(e) => { e.stopPropagation(); setStage('hidden'); }} data-id="insight-chat-decline">Not now</span>
                </div>
            </div>
        );
    }

    if (stage === 'down-reason') {
        return (
            <div className={classes.foot}>
                <div className={`${classes.promptLabel} ${classes.promptLabelStrong}`}>Thanks! What didn't work?</div>
                <div className={classes.reasons}>
                    {NOT_USEFUL_REASONS.map(reason => (
                        <button key={reason.key} className={classes.chip} onClick={(e) => onReason(e, reason.key)} data-id={`insight-reason-${reason.key}`}>{reason.label}</button>
                    ))}
                </div>
                <div className={classes.skip} onClick={(e) => onReason(e)} data-id="insight-reason-skip">Skip</div>
            </div>
        );
    }

    return (
        <div className={`${classes.foot} ${classes.askRow}`}>
            <span className={classes.askLabel}>Was this helpful?</span>
            <button className={classes.thumb} onClick={onThumbUp} data-id="insight-thumb-up">&#128077;</button>
            <button className={classes.thumb} onClick={onThumbDown} data-id="insight-thumb-down">&#128078;</button>
        </div>
    );
};
